import type {Pointer} from '@wix/document-services-types'

export const RICH_TEXT_PROPERTY = 'richText' as const

export interface BrandContext {
    voice: string
    colors: string[]
    fonts: string[]
    layoutStyle?: string
    mediaStyle?: string
}

export interface BusinessContext {
    siteName: string
    siteDescription: string
    industry?: string
    services?: string[]
    location?: string
    keywords?: string[]
}

export interface SiteContext {
    brand: BrandContext
    businessContext: BusinessContext
}

export interface SiteMetadata {
    siteName?: string
    brandColors?: string[]
    brandFonts?: string[]
}

export interface StylePropertyValue {
    value: string
    cssVariable?: string
    resolvedValue?: string
    innerElementId?: string
}

export interface ComponentStyleData {
    componentId: string
    colors: string[]
    backgroundColor?: StylePropertyValue
    textColor?: StylePropertyValue
    font?: StylePropertyValue
}

export interface ThemeVariable {
    cssVarName: string
    resolvedValue: string
}

export interface StyleData {
    componentStyles: ComponentStyleData[]
    themeVariables?: ThemeVariable[]
}

export interface AnalyzeParams {
    targetPointer: Pointer
    siteContext: SiteContext
}

export interface ComponentFontData {
    componentId: string
    fonts: string[]
}

export interface ExtractedFontData {
    componentFonts: ComponentFontData[]
}

export interface ComponentLogicIssue {
    componentId: string
    componentType: string
    issue: 'MISSING_LINK'
    details?: Record<string, any>
}

export interface ComponentLogicData {
    issues: ComponentLogicIssue[]
}

export type InsightType =
    | 'TEXT_DOMAIN_MISMATCH'
    | 'COLOR_INCONSISTENCY'
    | 'TONE_OF_VOICE_MISMATCH'
    | 'TYPOGRAPHY_INCONSISTENCY'
    | 'BUTTON_WITHOUT_LINK'

export interface InsightDetails {
    message: string
    dataType?: 'richText' | 'label' | 'text'
    innerElementPath?: string[]
    suggestedCssVariable?: string
}

export interface Insight {
    id: string
    componentPointer: Pointer
    type: InsightType
    confidence: number
    impact: number
    details: InsightDetails
}

export interface AnalysisResult {
    insights: Insight[]
}

export interface RecommendParams {
    analysisResult: AnalysisResult
    maxRecommendations?: number
    scoreThreshold?: number
}

export enum RecommendationActionType {
    TEXT = 'TEXT',
    STYLE = 'STYLE',
    INNER_ELEMENT_STYLE = 'INNER_ELEMENT_STYLE'
}

export interface RecommendedAction {
    type: RecommendationActionType
    data: Record<string, any>
    innerElementPath?: string[]
}

export interface RecommendationScores {
    impact: number
    confidence: number
    readiness: number
    final: number
}

export interface Recommendation {
    id: string
    componentPointer: Pointer
    message: string
    description: string
    action: RecommendedAction
    scores: RecommendationScores
    insightId: string
    insightType: InsightType
}

export interface RecommendationResult {
    recommendations: Recommendation[]
    limit?: number
    appliedThreshold?: number
}

export interface ApplyRecommendationResult {
    success: boolean
    componentPointer: Pointer
    appliedValue: any
    error?: string
}

export type ApplyRecommendationsResult = ApplyRecommendationResult[]

export interface SuggestParams {
    targetPointer: Pointer
    maxRecommendations?: number
    scoreThreshold?: number
}
